import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import Layout from '../Layout'
import Pagination from '../../../components/Pagination'

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    year:  'numeric',
    month: 'short',
    day:   'numeric',
  })
}

function RenameForm({ project, onCancel }) {
  const [name, setName] = useState(project.name)

  function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim()) return
    router.patch(`/settings/projects/${project.slug}`, { name: name.trim() }, {
      preserveScroll: true,
      onSuccess: onCancel,
    })
  }

  return (
    <div className="mt-2 space-y-1">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
        />
        <button
          type="submit"
          className="px-3 py-1.5 rounded-lg bg-emerald-800/60 hover:bg-emerald-700/80 text-emerald-200 border border-emerald-700/40 text-xs font-mono transition-colors shrink-0"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-100 text-xs font-mono transition-colors shrink-0"
        >
          Cancel
        </button>
      </form>
      {project.open_session_count > 0 && (
        <p className="text-xs font-mono text-amber-400">
          ⚠ {project.open_session_count} open session(s) — active agents referencing this slug may be affected.
        </p>
      )}
    </div>
  )
}

function ProjectRow({ project }) {
  const [renaming, setRenaming] = useState(false)
  const [expanded, setExpanded] = useState(false)

  function handleDelete() {
    if (!confirm(`Delete project '${project.name}'? This will permanently destroy ${project.memory_count} memories. This cannot be undone.`)) return
    router.delete(`/settings/projects/${project.slug}`, { preserveScroll: true })
  }

  return (
    <>
      <tr className="hover:bg-zinc-800/30 transition-colors">
        <td className="px-4 py-3 text-zinc-100">
          <div>
            <Link
              href={`/settings/projects/${project.slug}`}
              className="hover:text-emerald-400 transition-colors"
            >
              {project.name}
            </Link>
            {renaming && (
              <RenameForm project={project} onCancel={() => setRenaming(false)} />
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{project.slug}</td>
        <td className="px-4 py-3 text-zinc-400 text-center">{project.memory_count}</td>
        <td className="px-4 py-3 text-zinc-400 text-center">{project.open_session_count}</td>
        <td className="px-4 py-3 text-zinc-400">{formatDate(project.last_memory_at)}</td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setExpanded(v => !v); setRenaming(false) }}
              className="px-2 py-1 rounded text-xs text-zinc-400 bg-zinc-800 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
            >
              Sessions
            </button>
            <button
              onClick={() => { setRenaming(v => !v); setExpanded(false) }}
              className="px-2 py-1 rounded text-xs text-zinc-400 bg-zinc-800 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
            >
              Rename
            </button>
            <button
              onClick={handleDelete}
              className="px-2 py-1 rounded text-xs text-red-400 border border-red-900 hover:bg-red-950/60 hover:text-red-300 transition-colors"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-zinc-800/20">
          <td colSpan={6} className="px-6 py-3 text-sm font-mono text-zinc-500">
            <Link
              href={`/settings/projects/${project.slug}`}
              className="hover:text-emerald-400 transition-colors"
            >
              View sessions on the project detail page →
            </Link>
          </td>
        </tr>
      )}
    </>
  )
}

export default function Index({ projects = [], pagination = {} }) {
  return (
    <Layout active="projects">
      <div className="max-w-4xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-mono font-bold text-zinc-100">Projects</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Projects group memories by agent workspace — created automatically on first inscription, or manually below.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              href="/settings/projects/new"
              className="px-3 py-1.5 rounded-lg text-xs font-mono text-emerald-400 border border-emerald-800/60 hover:bg-emerald-900/30 hover:text-emerald-300 transition-colors"
            >
              New project
            </Link>
            <a
              href="/settings/export"
              download
              className="px-3 py-1.5 rounded-lg text-xs font-mono text-emerald-400 border border-emerald-800/60 hover:bg-emerald-900/30 hover:text-emerald-300 transition-colors"
            >
              Export workspace
            </a>
          </div>
        </div>

        {projects.length === 0 ? (
          <p className="text-sm font-mono text-zinc-600 py-4">
            No projects yet. Create one above, or agents will create one automatically on first inscription.
          </p>
        ) : (
          <section className="border border-zinc-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-800/40">
                  <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                    Name
                  </th>
                  <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="text-center px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                    Memories
                  </th>
                  <th className="text-center px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                    Open Sessions
                  </th>
                  <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-700">
                {projects.map(project => (
                  <ProjectRow key={project.id} project={project} />
                ))}
              </tbody>
            </table>
          </section>
        )}

        <Pagination
          current_page={pagination.current_page}
          total_pages={pagination.total_pages}
          onPageChange={page => router.get('/settings/projects', { page }, { preserveState: true })}
        />
      </div>
    </Layout>
  )
}
